import { openAlertDialog } from '@Common/handlers/services';
import { useIsomorphicEffect } from '@Common/hooks';
import { RootState } from '@Common/models/redux/store';
import { ExitToAppOutlined, PersonOutlined, PersonOutlineOutlined } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import {
  Divider,
  Grid,
  Icon,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from '@quillbot/design-system';
import Link from '@Common/components/Link';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Firestore from 'src/authentication/handlers/firestore';

const useStyles = makeStyles((theme) => ({
  popoverContent: {
    pointerEvents: 'auto',
    borderRadius: 8,
  },
  mainText: {
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '18px',
    color: theme.specialColors.ICON_DEFAULT_COLOR,
  },
  subText: {
    fontSize: '13px',
    lineHeight: '18px',
    color: theme.specialColors.ICON_DEFAULT_COLOR,
  },
  userIcon: {
    width: 24,
    height: 24,
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.specialColors.ICON_DEFAULT_COLOR,
    textTransform: 'uppercase',
  },
  mainMenuBtn: {
    padding: '4px',
    background: theme.palette.background.default,
    marginRight: 0,
    marginLeft: '12px',
    width: 32,
    height: 32,
    '&:hover': {
      background: theme.palette.background.default,
    },
  },
  menuItem: {
    display: 'flex',
    padding: '11px 16px',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': { background: theme.palette.background.default },
  },
}));

const AccountMenu = (props: { iconButtonProps?: any; clickable?: boolean }) => {
  const classes = useStyles();
  const theme = useTheme();
  const userState = useSelector((rootState: RootState) => rootState.user);
  const router = useRouter();
  const buttonRef = useRef(null);
  const inputText = useSelector(
    (rootState: RootState) => rootState?.pphr?.inputSentences?.sentenceList
  );

  const isExtension = useSelector((rootState: RootState) => rootState.common.platform.isExtension);
  const [anchorEl, setAnchorEl] = useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState<boolean>(false);

  const openAccountMenu = () => {
    if (userState.isAuthenticated) {
      setAccountMenuOpen(true);
      setAnchorEl(buttonRef.current);
    }
  };
  const closeAccountMenu = () => {
    setAccountMenuOpen(false);
    setAnchorEl(null);
  };

  const logOut = () => {
    closeAccountMenu();
    const logoutHandler = () => {
      Firestore.getInstance().signOut();
    };

    if (inputText.length && router.pathname === '/') {
      openAlertDialog(
        true,
        'Confirm Logout',
        'Logging out will delete all your text. Please copy it before continuing.',
        'Logout',
        logoutHandler
      );
    } else logoutHandler();
  };

  return (
    <>
      {!userState ||
        (!userState.isAuthenticated && (
          <Link
            href={{
              pathname: '/login',
              query: { referrer: 'header', returnUrl: router.pathname },
            }}
          >
            {/* Increasing FID if using tooltip, needs to be fixed */}
            {/* <Tooltip title="Log In">  */}
            <IconButton
              edge="end"
              className={classes.mainMenuBtn}
              aria-haspopup="true"
              size="large"
              title="Log In"
              {...props?.iconButtonProps}
            >
              <PersonOutlineOutlined />
            </IconButton>
            {/* </Tooltip> */}
          </Link>
        ))}
      {userState.isAuthenticated && (
        <IconButton
          ref={buttonRef}
          edge="end"
          className={classes.mainMenuBtn}
          aria-haspopup="true"
          onMouseEnter={props.clickable ? () => {} : openAccountMenu}
          onMouseLeave={props.clickable ? () => {} : closeAccountMenu}
          onClick={() => (accountMenuOpen ? closeAccountMenu() : openAccountMenu())}
          size="large"
          {...props?.iconButtonProps}
        >
          <div className={classes.userIcon}>
            {userState.fullName?.charAt(0) || userState.email?.charAt(0)}
          </div>
        </IconButton>
      )}

      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        classes={{
          paper: classes.popoverContent,
        }}
        open={accountMenuOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        PaperProps={{ onMouseEnter: openAccountMenu, onMouseLeave: closeAccountMenu }}
      >
        <Grid
          container
          flexDirection="column"
          sx={{
            width: 240,
            border: `1px solid ${theme.palette.divider}`,
            boxSizing: 'border-box',
            boxShadow: theme.shadows[5],
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Grid item sx={{ padding: '11px 16px' }}>
            <Typography className={classes.mainText}>Email</Typography>
            <Typography className={classes.subText}>{userState?.email}</Typography>
          </Grid>
          <Divider />
          <Link href="/settings">
            <a target={isExtension ? '_blank' : ''} style={{ textDecoration: 'none' }}>
              <Grid item className={classes.menuItem}>
                <Icon sx={{ marginRight: '10px' }}>
                  <PersonOutlined style={{ color: theme.specialColors.ICON_DEFAULT_COLOR }} />
                </Icon>
                <Typography className={classes.mainText}>Account Details</Typography>
              </Grid>
            </a>
          </Link>
          <Divider />
          <Grid item className={classes.menuItem} onClick={logOut}>
            <Icon sx={{ marginRight: '10px' }}>
              <ExitToAppOutlined style={{ color: theme.specialColors.ICON_DEFAULT_COLOR }} />
            </Icon>
            <Typography className={classes.mainText}>Log Out</Typography>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default AccountMenu;
